import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, Input, Button, Select, Badge, Icon, HStack,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Skeleton, useToast,
  Tag, TagLabel, Tooltip, InputGroup, InputLeftElement,
  Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { Card, Title } from "@tremor/react";
import {
  RefreshCw, Calendar, Search, ArrowUpDown, CheckCircle2, AlertCircle,
  Download, RotateCcw, Package
} from 'lucide-react';
import { DRE_ORIGENS, paramsDaOrigem } from '../utils/dreOrigens';
import ScrollToTop from "../components/ScrollToTop";
import { getDreDetalhado } from "../services/api";

const formatMoney = (val) => `R$ ${parseFloat(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const formatMoneyRaw = (val) => parseFloat(val || 0).toFixed(2).replace('.', ',');
const formatPercent = (val) => `${parseFloat(val || 0).toFixed(2)}%`;
const formatDate = (val) => val ? new Date(val).toLocaleDateString('pt-BR') : '-';

const exportCSV = (data, filename, tipo = 'normais') => {
  const headers = ['ID Origem', 'ID Venda', 'Data', 'Origem', 'Conta', 'Receita Bruta', 'CMV', 'Taxa ML / MP', 'Frete', 'Impostos', 'Lucro Líquido', 'Margem %', 'Sincronizado'];
  if (tipo === 'devolvidas') headers.push('Devolução Recebida', 'Origem Devolução');
  if (tipo === 'estoque') headers.push('Valor Estoque Taga');

  const rows = data.map(v => {
    const base = [
      v.id_origem || '',
      v.venda_id || '',
      v.data_venda ? v.data_venda.substring(0, 10).split('-').reverse().join('/') : '',
      v.origem || '',
      v.conta || '',
      formatMoneyRaw(v.receita_bruta),
      formatMoneyRaw(v.cmv),
      formatMoneyRaw(v.taxa_ml),
      formatMoneyRaw(v.frete),
      formatMoneyRaw(v.impostos),
      formatMoneyRaw(v.lucro_liquido),
      parseFloat(v.margem || 0).toFixed(2).replace('.', ','),
      v.sincronizado ? 'Sim' : 'Não',
    ];
    if (tipo === 'devolvidas') {
      base.push(
        v.devolucao_received_at ? v.devolucao_received_at.substring(0, 10).split('-').reverse().join('/') : '',
        v.devolucao_origem || ''
      );
    }
    if (tipo === 'estoque') {
      base.push(formatMoneyRaw(v.valor_estoque_taga));
    }
    return base;
  });

  const BOM = '\uFEFF';
  const csv = BOM + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Mesmo conceito do filtro de origem: conta só faz sentido no Mercado Livre. */
const nomeDoCanal = (origem, conta) => {
  if (origem === 'Mercado Livre') return `Mercado Livre ${conta || '?'}`;
  if (origem === 'ECOMMERCE') return 'E-commerce';
  if (origem === 'TAGA') return 'TAGA (Manual)';
  return origem || '-';
};

const origemColors = {
  'Mercado Livre': 'yellow',
  'ECOMMERCE': 'blue',
  'TAGA': 'orange',
};

const fonteLabel = {
  'nfe_entrada': 'NFe',
  'valor_compra': 'Valor Compra',
  'calculada': 'Calculada',
  'api': 'API ML',
  'api_flex': 'API ML Flex',
  'envios_table': 'BD Local',
  'melhor_envio': 'Melhor Envio',
  'lalamove': 'Lalamove',
  'me_lalamove': 'Melhor Envio + Lalamove',
  'retirada': 'Retirada na loja (sem frete)',
  'sem_custo_frete': 'Sem custo conhecido (fora da conta)',
  'nao_disponivel': 'N/D',
  'mercadopago': 'Mercado Pago',
  'mercadopago_falha': 'Mercado Pago (falha na consulta)',
  'mercadopago_sem_pagamento': 'Mercado Pago (sem pagamento aprovado)',
};

const DREDetalhado = () => {
  const toast = useToast();
  const dataFetchedRef = useRef(false);

  const hoje = new Date();
  const seteDiasAtras = new Date(hoje);
  seteDiasAtras.setDate(hoje.getDate() - 7);

  const [dataInicio, setDataInicio] = useState(seteDiasAtras.toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState(hoje.toISOString().slice(0, 10));
  const [origemFiltro, setOrigemFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [vendas, setVendas] = useState([]);
  const [busca, setBusca] = useState('');
  const [sortField, setSortField] = useState('data_venda');
  const [sortDir, setSortDir] = useState('desc');
  const [tipo, setTipo] = useState('normais');

  const fetchDetalhado = async (tipoAtual = tipo) => {
    setLoading(true);
    const params = { data_inicio: dataInicio, data_fim: dataFim, tipo: tipoAtual };
    Object.assign(params, paramsDaOrigem(origemFiltro));

    const res = await getDreDetalhado(params);
    if (res && res.data) {
      setVendas(res.data);
    } else if (res === false) {
      toast({ title: 'Erro ao carregar dados detalhados', status: 'error', duration: 3000 });
      setVendas([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchDetalhado(tipo);
      dataFetchedRef.current = true;
    }
  }, []);

  const handleTabChange = (idx) => {
    const novoTipo = ['normais', 'devolvidas', 'estoque'][idx];
    setTipo(novoTipo);
    setBusca('');
    fetchDetalhado(novoTipo);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filteredVendas = vendas
    .filter(v => {
      if (!busca) return true;
      const term = busca.toLowerCase();
      return (
        String(v.venda_id).includes(term) ||
        String(v.id_origem).toLowerCase().includes(term) ||
        v.origem?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  // Totais
  const totais = filteredVendas.reduce((acc, v) => ({
    receita_bruta: acc.receita_bruta + (v.receita_bruta || 0),
    cmv: acc.cmv + (v.cmv || 0),
    taxa_ml: acc.taxa_ml + (v.taxa_ml || 0),
    frete: acc.frete + (v.frete || 0),
    impostos: acc.impostos + (v.impostos || 0),
    lucro_liquido: acc.lucro_liquido + (v.lucro_liquido || 0),
  }), { receita_bruta: 0, cmv: 0, taxa_ml: 0, frete: 0, impostos: 0, lucro_liquido: 0 });

  const margemMedia = totais.receita_bruta > 0 ? (totais.lucro_liquido / totais.receita_bruta) * 100 : 0;

  const SortHeader = ({ field, children }) => (
    <Th
      cursor="pointer" onClick={() => handleSort(field)}
      _hover={{ bg: 'gray.100' }} userSelect="none" fontSize="xs"
    >
      <Flex align="center" gap={1}>
        {children}
        {sortField === field && (
          <Text fontSize="xs" color="blue.500">{sortDir === 'asc' ? '↑' : '↓'}</Text>
        )}
      </Flex>
    </Th>
  );

  return (
    <Box className="min-h-screen bg-gray-50" pt="60px" ml={{ base: 0, md: '64px' }} overflowX="hidden">
      <ScrollToTop />
      <Box maxW="100%" px={{ base: 3, md: 5 }} py={6}>
        {/* Header */}
        <Box mb={4}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">DRE Detalhado</Text>
          <Text fontSize="sm" color="gray.500">Breakdown financeiro por venda individual</Text>
        </Box>

        {/* Tabs */}
        <Tabs
          index={['normais', 'devolvidas', 'estoque'].indexOf(tipo)}
          onChange={handleTabChange}
          colorScheme="blue"
          variant="enclosed"
          mb={4}
          isLazy
        >
          <TabList>
            <Tab fontSize="sm">
              <HStack spacing={2}>
                <Icon as={CheckCircle2} boxSize={4} />
                <Text>Vendas</Text>
              </HStack>
            </Tab>
            <Tab fontSize="sm">
              <HStack spacing={2}>
                <Icon as={RotateCcw} boxSize={4} />
                <Text>Devolvidas</Text>
              </HStack>
            </Tab>
            <Tab fontSize="sm">
              <HStack spacing={2}>
                <Icon as={Package} boxSize={4} />
                <Text>Vendas de Estoque</Text>
              </HStack>
            </Tab>
          </TabList>
        </Tabs>

        {tipo === 'devolvidas' && (
          <Text fontSize="xs" color="gray.500" mb={3}>
            Vendas no período que tiveram devolução registrada (qualquer origem). Apenas listagem visual — não interfere nos cálculos do DRE consolidado.
          </Text>
        )}
        {tipo === 'estoque' && (
          <Text fontSize="xs" color="gray.500" mb={3}>
            Vendas no período que consumiram itens do estoque taga (mesmo que parcialmente). Apenas listagem visual — não interfere nos cálculos do DRE consolidado.
          </Text>
        )}

        {/* Filtros */}
        <Card className="ring-0 mb-6">
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Início</Text>
              <Input type="date" size="sm" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Fim</Text>
              <Input type="date" size="sm" value={dataFim} onChange={(e) => setDataFim(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Origem</Text>
              <Select size="sm" value={origemFiltro} onChange={(e) => setOrigemFiltro(e.target.value)} bg="white" borderRadius="md" placeholder="Todas">
                {DRE_ORIGENS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Box>
            <Button size="sm" colorScheme="blue" leftIcon={<RefreshCw size={14} />} onClick={() => fetchDetalhado(tipo)} isLoading={loading} px={6}>
              Filtrar
            </Button>
            {filteredVendas.length > 0 && (
              <Button
                size="sm" variant="outline" colorScheme="green"
                leftIcon={<Download size={14} />}
                onClick={() => exportCSV(filteredVendas, `dre-detalhado-${tipo}-${dataInicio}-${dataFim}.csv`, tipo)}
              >
                Exportar CSV
              </Button>
            )}
          </Flex>
        </Card>

        {/* Totalizadores */}
        {!loading && filteredVendas.length > 0 && (
          <Flex gap={4} mb={4} flexWrap="wrap">
            <Tag size="lg" colorScheme="blue" borderRadius="full" px={4}>
              <TagLabel>{filteredVendas.length} vendas</TagLabel>
            </Tag>
            <Tag size="lg" colorScheme="green" borderRadius="full" px={4}>
              <TagLabel>Receita: {formatMoney(totais.receita_bruta)}</TagLabel>
            </Tag>
            <Tag size="lg" colorScheme={totais.lucro_liquido >= 0 ? "green" : "red"} borderRadius="full" px={4}>
              <TagLabel>Lucro: {formatMoney(totais.lucro_liquido)}</TagLabel>
            </Tag>
            <Tag size="lg" colorScheme="purple" borderRadius="full" px={4}>
              <TagLabel>Margem Média: {formatPercent(margemMedia)}</TagLabel>
            </Tag>
          </Flex>
        )}

        {/* Busca */}
        <Flex mb={4}>
          <InputGroup size="sm" maxW="300px">
            <InputLeftElement><Search size={14} color="gray" /></InputLeftElement>
            <Input
              placeholder="Buscar por ID, origem..."
              value={busca} onChange={(e) => setBusca(e.target.value)}
              bg="white" borderRadius="md"
            />
          </InputGroup>
        </Flex>

        {/* Tabela */}
        <Card className="ring-0">
          <TableContainer overflowX="auto" maxW="100%">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr bg="gray.50">
                  <SortHeader field="id_origem">ID Origem</SortHeader>
                  <SortHeader field="venda_id">ID Venda</SortHeader>
                  <SortHeader field="data_venda">Data</SortHeader>
                  <Th fontSize="xs">Canal</Th>
                  <SortHeader field="receita_bruta">Receita</SortHeader>
                  <SortHeader field="cmv">CMV</SortHeader>
                  <SortHeader field="taxa_ml">Taxa ML / MP</SortHeader>
                  <SortHeader field="frete">Frete</SortHeader>
                  <SortHeader field="impostos">Impostos</SortHeader>
                  <SortHeader field="lucro_liquido">Lucro</SortHeader>
                  <SortHeader field="margem">Margem</SortHeader>
                  <Th fontSize="xs">Sync</Th>
                  {tipo === 'devolvidas' && <Th fontSize="xs">Devolução</Th>}
                  {tipo === 'estoque' && <SortHeader field="valor_estoque_taga">Vlr Estoque</SortHeader>}
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  [...Array(10)].map((_, i) => (
                    <Tr key={i}>
                      {[...Array(12 + (tipo !== 'normais' ? 1 : 0))].map((_, j) => (
                        <Td key={j}><Skeleton h="16px" w="60px" /></Td>
                      ))}
                    </Tr>
                  ))
                ) : filteredVendas.length === 0 ? (
                  <Tr>
                    <Td colSpan={12 + (tipo !== 'normais' ? 1 : 0)}>
                      <Text color="gray.400" textAlign="center" py={8}>Nenhuma venda encontrada no período</Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredVendas.map((v) => (
                    <Tr
                      key={v.venda_id}
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.15s"
                      bg={tipo === 'devolvidas' ? 'red.50' : tipo === 'estoque' ? 'purple.50' : undefined}
                    >
                      <Td fontSize="xs" fontWeight="medium">{v.id_origem || '-'}</Td>
                      <Td fontSize="xs" color="gray.600">#{v.venda_id}</Td>
                      <Td fontSize="xs" color="gray.600">
                        {v.data_venda ? new Date(v.data_venda).toLocaleDateString('pt-BR') : '-'}
                      </Td>
                      <Td>
                        <Badge colorScheme={origemColors[v.origem] || 'gray'} fontSize="xs" borderRadius="full" px={2}>
                          {nomeDoCanal(v.origem, v.conta)}
                        </Badge>
                      </Td>
                      <Td fontSize="xs" fontWeight="medium" color="blue.600">{formatMoney(v.receita_bruta)}</Td>
                      <Td fontSize="xs">
                        <Tooltip label={`Fonte: ${fonteLabel[v.cmv_fonte] || v.cmv_fonte}`} placement="top">
                          <Text cursor="help" color="red.500">{formatMoney(v.cmv)}</Text>
                        </Tooltip>
                      </Td>
                      <Td fontSize="xs">
                        <Tooltip label={`Fonte: ${fonteLabel[v.taxa_ml_fonte] || v.taxa_ml_fonte}`} placement="top">
                          <Text cursor="help" color="orange.500">{formatMoney(v.taxa_ml)}</Text>
                        </Tooltip>
                      </Td>
                      <Td fontSize="xs">
                        <Tooltip label={`Fonte: ${fonteLabel[v.frete_fonte] || v.frete_fonte}`} placement="top">
                          <Text cursor="help" color="yellow.600">{formatMoney(v.frete)}</Text>
                        </Tooltip>
                      </Td>
                      <Td fontSize="xs" color="purple.500">{formatMoney(v.impostos)}</Td>
                      <Td fontSize="xs" fontWeight="bold" color={v.lucro_liquido >= 0 ? 'green.600' : 'red.600'}>
                        {formatMoney(v.lucro_liquido)}
                      </Td>
                      <Td fontSize="xs">
                        <Badge
                          colorScheme={v.margem >= 20 ? 'green' : v.margem >= 10 ? 'yellow' : v.margem >= 0 ? 'orange' : 'red'}
                          fontSize="xs" borderRadius="full" px={2}
                        >
                          {formatPercent(v.margem)}
                        </Badge>
                      </Td>
                      <Td>
                        {v.sincronizado ? (
                          <Icon as={CheckCircle2} boxSize={4} color="green.500" />
                        ) : (
                          <Icon as={AlertCircle} boxSize={4} color="orange.400" />
                        )}
                      </Td>
                      {tipo === 'devolvidas' && (
                        <Td fontSize="xs">
                          <Tooltip label={`Origem: ${v.devolucao_origem || '-'}`} placement="top">
                            <HStack spacing={1} cursor="help">
                              <Icon as={RotateCcw} boxSize={3} color="red.400" />
                              <Text color="red.600">{formatDate(v.devolucao_received_at)}</Text>
                            </HStack>
                          </Tooltip>
                        </Td>
                      )}
                      {tipo === 'estoque' && (
                        <Td fontSize="xs" color="purple.700" fontWeight="medium">
                          {formatMoney(v.valor_estoque_taga)}
                        </Td>
                      )}
                    </Tr>
                  ))
                )}
              </Tbody>
              {!loading && filteredVendas.length > 0 && (
                <Tbody>
                  <Tr bg="gray.100" fontWeight="bold">
                    <Td colSpan={4} fontSize="xs">TOTAL</Td>
                    <Td fontSize="xs" color="blue.700">{formatMoney(totais.receita_bruta)}</Td>
                    <Td fontSize="xs" color="red.600">{formatMoney(totais.cmv)}</Td>
                    <Td fontSize="xs" color="orange.600">{formatMoney(totais.taxa_ml)}</Td>
                    <Td fontSize="xs" color="yellow.700">{formatMoney(totais.frete)}</Td>
                    <Td fontSize="xs" color="purple.600">{formatMoney(totais.impostos)}</Td>
                    <Td fontSize="xs" color={totais.lucro_liquido >= 0 ? 'green.700' : 'red.700'}>
                      {formatMoney(totais.lucro_liquido)}
                    </Td>
                    <Td fontSize="xs">
                      <Badge colorScheme={margemMedia >= 0 ? 'green' : 'red'} borderRadius="full" px={2}>
                        {formatPercent(margemMedia)}
                      </Badge>
                    </Td>
                    <Td />
                    {tipo === 'devolvidas' && <Td />}
                    {tipo === 'estoque' && (
                      <Td fontSize="xs" color="purple.700">
                        {formatMoney(filteredVendas.reduce((acc, v) => acc + (v.valor_estoque_taga || 0), 0))}
                      </Td>
                    )}
                  </Tr>
                </Tbody>
              )}
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default DREDetalhado;
